import type { MetaDescriptor } from 'react-router';

import { createI18nFn } from 'i18n/createI18nFn';
import type { LayoutHandleProps } from 'layouts/Layout/LayoutHandleProps';
import { orgDashboardApiIsomorphicFetchOrg } from 'modules/orgDashboard/api/orgDashboardApiFetchOrg';
import { orgDashboardManageListingsIsomorphicSearch } from 'modules/orgDashboard/hooks/orgDashboardManageListingsIsomorphicSearch';
import { orgDashboardManageListingsRoute } from 'modules/orgDashboard/routing/orgDashboard.routes';
import type { HorizonAppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from 'routing/types/HorizonAppRouteComponentProps';
import type { HorizonRouteMetaArgs } from 'routing/types/HorizonRouteMetaArgs';
import { getAppRouteSearchParams } from 'routing/utils/getAppRouteSearchParams';

import { OrgDashboardManageListingsPageLoadable } from './OrgDashboardManageListingsPageLoadable';

export const handle = {
  fallbackUrlByLocaleToCurrent: true,
  darkBackground: true,
  showDesktopSearch: true,
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
  params: { orgId },
}: HorizonAppRouteLoaderArgs<typeof orgDashboardManageListingsRoute>) {
  const searchParams = getAppRouteSearchParams({
    url: request.url,
    route: orgDashboardManageListingsRoute,
  });

  const pageIndex = parseInt(searchParams.page ?? '1', 10) - 1;
  const status = searchParams.status ?? 'ALL';
  const query = searchParams.query ?? '';
  const type = searchParams.type ?? 'ALL';

  const [{ org, user: orgUserPreferences }] = await Promise.all([
    orgDashboardApiIsomorphicFetchOrg({
      request,
      context,
    })(orgId),
    OrgDashboardManageListingsPageLoadable.load(),
  ]);

  const resultsPromise = orgDashboardManageListingsIsomorphicSearch({
    searchApiKey: org.searchApiKey,
    type,
    query,
    pageIndex,
    status,
  });

  return {
    org,
    preferredListingTypes: orgUserPreferences.preferredListingTypes ?? [],
    query,
    pageIndex,
    status,
    type,
    resultsPromise,
    locale: context.locale,
  };
}

export function meta({
  loaderData: { locale },
}: HorizonRouteMetaArgs<typeof loader>): MetaDescriptor[] {
  const i18n = createI18nFn(locale);
  return [{ title: i18n('Manage Listings') }];
}

export default function OrgDashboardManageListingsHorizonRoute({
  loaderData: {
    org,
    preferredListingTypes,
    query,
    pageIndex,
    status,
    type,
    resultsPromise,
  },
}: HorizonAppRouteComponentProps<typeof loader>) {
  return (
    <OrgDashboardManageListingsPageLoadable
      org={org}
      preferredListingTypes={preferredListingTypes}
      query={query}
      pageIndex={pageIndex}
      status={status}
      type={type}
      resultsPromise={resultsPromise}
    />
  );
}
